import { tenant } from '@tenant';
import { default as Checkbox } from 'antd/es/checkbox';
import { default as Modal } from 'antd/es/modal';
import { default as QRCode } from 'antd/es/qr-code';
import { default as Select } from 'antd/es/select';
import { CopyIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { CryptoFlag } from 'shared/src/CryptoFlag';
import { CryptoNetworkIcon } from 'shared/src/CryptoNetworkIcon';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import { hasValue } from 'utils/hasValue';

import type { GetCryptoBalances200 } from 'api/customerApi';
import { useGetCryptoDepositAddresses } from 'api/customerApi';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { CryptoAddress } from '@atoms/CryptoAddress/CryptoAddress';
import { Spinner } from '@atoms/Spinner/Spinner';

import { getNetworkDisplayName } from '../config';
import {
  CRYPTO_SELECTOR_STALE_TIME,
  CurrencyLabel,
  CurrencyOption,
  useCryptoSelection
} from '../shared/useCryptoSelection';
import { useVirtualBankAccount } from '../shared/VirtualBankAccountProvider';
import { buildQRCode } from '../utils';

type Props = {
  balances?: GetCryptoBalances200;
};

export const DepositTab = ({ balances: sharedBalances }: Props) => {
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [showQrCode, setShowQrCode] = useState(false);

  const {
    organisationId,
    balances,
    currencyOptions,
    isCurrencyOptionsLoading,
    isNetworkOptionsLoading,
    networkOptions,
    selectedAsset,
    selectedAddressId,
    setSelectedAddressId,
    selectedNetwork,
    handleCurrencyChange
  } = useCryptoSelection({
    flow: 'deposit',
    balances: sharedBalances,
    shouldFetchBalances: false
  });
  const { virtualAccountNumber } = useVirtualBankAccount();
  const hasSelectedAssetAndNetwork =
    hasValue(selectedAsset) && hasValue(selectedNetwork);
  const hasResolvedAccount = hasValue(virtualAccountNumber);
  const { data: walletAddress } = useGetCryptoDepositAddresses(
    organisationId,
    {
      asset: selectedAsset ?? '',
      network: selectedNetwork ?? '',
      virtual_account_number: virtualAccountNumber
    },
    {
      query: {
        enabled: hasResolvedAccount && hasSelectedAssetAndNetwork,
        staleTime: CRYPTO_SELECTOR_STALE_TIME,
        refetchOnWindowFocus: false
      }
    }
  );
  const selectedAddress = walletAddress?.address;

  const qrCodeValue =
    hasValue(selectedAddress) &&
    hasValue(selectedAsset) &&
    hasValue(selectedNetwork)
      ? buildQRCode({
          depositAddress: selectedAddress,
          asset: selectedAsset,
          network: selectedNetwork
        })
      : undefined;

  const openNotification = useOpenNotification();

  useEffect(() => {
    setDisclaimerAccepted(false);
    setShowQrCode(false);
  }, [selectedAsset, selectedAddressId, virtualAccountNumber]);

  const success = useCallback(
    (text: string) => {
      openNotification({
        kind: 'success',
        message: `${text} has been copied to your clipboard`
      });
    },
    [openNotification]
  );

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex flex-1 flex-col">
        <div className="flex h-20 flex-col gap-1">
          <label className="text-xs font-semibold text-content-secondary">
            Currency
          </label>
          <Select
            placeholder="Select deposit currency"
            value={selectedAsset}
            optionRender={(x) => (
              <CurrencyOption
                asset={String(x.value)}
                ledgerBalances={balances?.data?.balances}
              />
            )}
            labelRender={(x) => (
              <CurrencyLabel
                asset={String(x.value)}
                ledgerBalances={balances?.data?.balances}
              />
            )}
            onChange={handleCurrencyChange}
            options={currencyOptions}
            loading={isCurrencyOptionsLoading}
            notFoundContent={
              isCurrencyOptionsLoading && (
                <div className="flex justify-center py-2">
                  <Spinner />
                </div>
              )
            }
            className="w-full"
          />
        </div>
        <div className="flex h-20 flex-col gap-1">
          {selectedAsset && (
            <>
              <label className="text-xs font-semibold text-content-secondary">
                Network
              </label>
              <Select
                placeholder="Select a network"
                value={selectedAddressId}
                onChange={setSelectedAddressId}
                options={networkOptions}
                loading={isNetworkOptionsLoading}
                notFoundContent={
                  isNetworkOptionsLoading && (
                    <div className="flex justify-center py-2">
                      <Spinner />
                    </div>
                  )
                }
                className="w-full"
              />
            </>
          )}
        </div>
        {walletAddress?.address === null && (
          <p className="text-sm text-content-secondary">
            No deposit address was configured, please contact support at{' '}
            <a href={`mailto:${tenant.support.email}`}>
              {tenant.support.email}
            </a>
          </p>
        )}
        {selectedAddress && (
          <div className="flex flex-col gap-4">
            <Checkbox
              checked={disclaimerAccepted}
              onChange={(e) => setDisclaimerAccepted(e.target.checked)}
            >
              <span className="text-xs text-content-secondary">
                By ticking this box I declare that the {selectedAsset} to be
                deposited is mine and obtained in a legal manner.
              </span>
            </Checkbox>
            {disclaimerAccepted && (
              <>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-semibold text-content-secondary">
                    Deposit address
                  </label>
                  <CryptoAddress address={selectedAddress} truncate={false} />
                </div>
                <WarningText
                  selectedAsset={selectedAsset}
                  selectedNetwork={selectedNetwork}
                />
              </>
            )}
          </div>
        )}
      </div>
      {selectedAddress && disclaimerAccepted && (
        <>
          <PrimaryButton
            className="mt-auto"
            disabled={!hasValue(selectedAsset) || !hasValue(selectedNetwork)}
            onClick={() => setShowQrCode(true)}
          >
            Show QR code
          </PrimaryButton>
          {selectedAsset && (
            <Modal
              title={null}
              open={showQrCode}
              onCancel={() => setShowQrCode(false)}
              footer={null}
            >
              <div className="flex flex-col items-center gap-4 py-4">
                <p className="text-xl text-content-primary">
                  Receive {selectedAsset}
                </p>
                {qrCodeValue && (
                  <div className="relative inline-flex">
                    <QRCode value={qrCodeValue} size={256} />
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <div className="relative inline-flex rounded-md bg-white p-1">
                        <CryptoFlag
                          cryptoCode={selectedAsset}
                          className="size-12"
                        />
                        <CryptoNetworkIcon network={selectedNetwork ?? ''} />
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex w-full flex-col gap-2">
                  <label className="text-xs font-semibold text-content-secondary">
                    Deposit address
                  </label>
                  <CopyToClipboard
                    text={selectedAddress}
                    onCopy={(text) => success(text)}
                  >
                    <div className="flex cursor-pointer flex-row items-center justify-between rounded-xl border p-3">
                      {selectedAddress}
                      <CopyIcon width={14} height={14} />
                    </div>
                  </CopyToClipboard>
                </div>
                <WarningText
                  selectedAsset={selectedAsset}
                  selectedNetwork={selectedNetwork}
                />
              </div>
            </Modal>
          )}
        </>
      )}
    </div>
  );
};

const WarningText = ({
  selectedAsset,
  selectedNetwork
}: {
  selectedAsset?: string;
  selectedNetwork?: string;
}) => {
  const displayNetwork = hasValue(selectedNetwork)
    ? getNetworkDisplayName(selectedNetwork)
    : selectedNetwork;

  return (
    <div className="rounded-lg bg-feedback-info-surface p-3">
      Please{' '}
      <span className="font-bold">
        deposit only {selectedAsset} ({displayNetwork}) to this account
      </span>
      . Sending any other coin or token to this address may result in the loss
      of your deposit.
    </div>
  );
};
